/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Box, Button, Dialog, DialogContent, DialogTitle, Grid, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible, setModalQuotationVisible } from "@store/slices/appSlice";
import { setNewConditionLoan } from "@store/slices/registerSlice";
import { ApiError, LoanCondiciones } from "@interfaces/response";
import { TaxSystem } from "@interfaces/catalogs";
import { useLazyGetTaxSystemsQuery } from "@api/catalogs";
import { useLazyGetConditionsByIdQuery, usePostConditionMutation } from "@api/applications";
import { QuotationOferta } from "@components/QuotationOferta";
import CenteredLoader from "@components/CenteredLoader";
import ModalCloseButton from "@components/ModalCloseButton";
import * as Sentry from "@sentry/react";

interface NewLoanCondition {
  has_change: boolean;
  amount: number;
  term: number;
  monthlyPayment: number;
}

interface CotizadorDefaultProps {
  condition: LoanCondiciones | null;
  taxSystems: TaxSystem | null;
  handleOnClickClose: () => void;
}

const CotizadorDefault = ({ condition, taxSystems, handleOnClickClose }: CotizadorDefaultProps) => {
  // hooks
  const dispatch = useAppDispatch();

  // redux hooks
  const loanApplication = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  // api hooks
  const [triggerPostCondition] = usePostConditionMutation();

  // state
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [newConditions, setNewConditions] = useState<NewLoanCondition>({
    has_change: false,
    amount: 0,
    term: 0,
    monthlyPayment: 0,
  });

  const handleOnUpdateValues = (amount: number, term: number, monthlyPayment: number) => {
    if (!condition) return;

    const has_change_amount = condition.selected_amount !== amount;
    const has_change_term = condition.selected_term !== term;

    setNewConditions({
      has_change: has_change_amount || has_change_term,
      amount: amount,
      term: term,
      monthlyPayment: monthlyPayment,
    });
  };

  const handleOnClickUpdateConditionLoan = async () => {
    try {
      setIsSaving(true);
      await triggerPostCondition({
        loan_application: loanApplication.id,
        loan_amount: newConditions.amount,
        requested_term: newConditions.term,
        selected_amount: newConditions.amount,
        selected_term: newConditions.term,
        requested_amount: newConditions.amount,
        user_name: `TOFU - ${accountUser.email}`,
        user_id: String(accountUser.id),
      }).unwrap();

      dispatch(
        setNewConditionLoan({
          approved_amount: loanApplication.approved_amount,
          approved_term: loanApplication.approved_term,
          interest_rate: loanApplication.interest_rate,
          loanAmount: newConditions.amount,
          loanTerm: newConditions.term,
          amountMonthly: newConditions.monthlyPayment,
        }),
      );
      dispatch(setModalQuotationVisible(false));
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          component: "CotizadorDefault",
        },
      });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogTitle sx={{ position: "relative", pr: 6, pt: 2 }}>
        <Typography variant="body1" color={"#002652"} fontWeight={600} textAlign={"center"}>
          Modifica tu cotización
        </Typography>
        <ModalCloseButton isSaving={isSaving} handleOnClickClose={handleOnClickClose} />
      </DialogTitle>
      <DialogContent>
        <Box mx={2}>
          <QuotationOferta condition={condition} taxSystems={taxSystems} onUpdateValues={handleOnUpdateValues} />

          <Grid container columnSpacing={1} my={2}>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="blue_outlined"
                sx={{ padding: "0", height: "100%" }}
                onClick={handleOnClickClose}
              >
                Regresar
              </Button>
            </Grid>
            <Grid item xs={6}>
              <Button
                fullWidth
                variant="default"
                size="medium"
                onClick={handleOnClickUpdateConditionLoan}
                disabled={isSaving || !newConditions.has_change}
              >
                Continuar
              </Button>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </>
  );
};

const QuotationModalDefault = () => {
  // hooks
  const dispatch = useAppDispatch();

  // redux hooks
  const modalState = useSelector((state: RootState) => state.app.modal_quotation);
  const loanApplication = useSelector((state: RootState) => state.register.application);

  // api hooks
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [getTaxSystems] = useLazyGetTaxSystemsQuery();

  // state
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [taxSystems, setTaxSystems] = useState<TaxSystem | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleOnClickClose = () => {
    dispatch(setModalQuotationVisible(false));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (modalState?.visible === false) return;

        setIsLoading(true);

        const [conditions, taxSystems] = await Promise.all([
          getConditions(loanApplication.id).unwrap(),
          getTaxSystems(loanApplication.type).unwrap(),
        ]);

        const activeCondition = conditions.find((item) => item.active);
        if (activeCondition) {
          setCondition(activeCondition);
          setTaxSystems(taxSystems);
        }

        console.log("application: ", loanApplication);
      } catch (error) {
        console.log("Ha ocurrido un error: ");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [modalState?.visible]);

  if (isLoading) {
    return (
      <Dialog open={modalState?.visible} maxWidth={"sm"}>
        <Box display={"flex"} justifyContent={"center"} alignItems={"center"} width={"280px"} height={"400px"}>
          <CenteredLoader />
        </Box>
      </Dialog>
    );
  }

  return (
    <Dialog open={modalState?.visible} maxWidth={"sm"}>
      <CotizadorDefault condition={condition} taxSystems={taxSystems} handleOnClickClose={handleOnClickClose} />
    </Dialog>
  );
};
export default QuotationModalDefault;
